import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Wallet, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Şifreler eşleşmiyor");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Şifreniz güncellendi. Yeni şifrenizle giriş yapabilirsiniz.");
      navigate("/login", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Wallet className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Usta Hesap</h1>
            <p className="text-sm text-muted-foreground">Şifre sıfırlama</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8" data-testid="reset-password-card">
          {!token ? (
            <div className="space-y-4 text-center">
              <p data-testid="reset-no-token-message" className="text-sm text-muted-foreground">
                Geçersiz bağlantı. Lütfen e-postanızdaki sıfırlama bağlantısını kullanın.
              </p>
              <Link to="/login" className="text-sm text-primary hover:underline" data-testid="reset-back-to-login">
                Giriş sayfasına dön
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <KeyRound className="w-4 h-4 text-primary" /> Yeni şifrenizi belirleyin
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">Yeni Şifre (en az 6 karakter)</Label>
                <Input id="new-password" data-testid="reset-password-input" type="password" required minLength={6}
                  placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Yeni Şifre (tekrar)</Label>
                <Input id="confirm-password" data-testid="reset-password-confirm-input" type="password" required minLength={6}
                  placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              {error && <p data-testid="reset-error-message" className="text-sm text-destructive">{error}</p>}
              <Button data-testid="reset-submit-button" type="submit" disabled={loading}
                className="w-full rounded-full font-semibold">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Şifremi Güncelle
              </Button>
              <p className="text-center">
                <Link to="/login" className="text-xs text-muted-foreground hover:text-primary" data-testid="reset-login-link">
                  Giriş sayfasına dön
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
